/**
 * useBrandExtractionStatus Hook
 *
 * Polls brand extraction job status and provides cancel/prioritize actions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/api';

export interface BrandExtractionStatus {
  jobId: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  queuePosition: number | null;
  totalInQueue?: number | null;
  estimatedStartTime: string | null;
  result?: {
    guideId: string;
    version: number;
    logoUrl?: string;
    confidence?: number;
  };
  error?: string;
  createdAt: string;
}

export interface UseBrandExtractionStatusReturn {
  status: BrandExtractionStatus | null;
  isLoading: boolean;
  error: string | null;
  cancel: () => Promise<void>;
  prioritize: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useBrandExtractionStatus(
  jobId: string | null
): UseBrandExtractionStatusReturn {
  const [status, setStatus] = useState<BrandExtractionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Fetch status from API
  const fetchStatus = useCallback(async () => {
    if (!jobId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get(`/brand/extract/status/${jobId}`);
      const data = response.data.data;

      if (isMountedRef.current) {
        setStatus(data);

        // Stop polling if job is completed or failed
        if (data.state === 'completed' || data.state === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        setError(err.response?.data?.message || 'Failed to fetch job status');

        // Stop polling on error
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [jobId]);

  // Start polling
  useEffect(() => {
    if (!jobId) return;

    isMountedRef.current = true;

    // Initial fetch
    fetchStatus();

    // Poll every 5 seconds (consistent with TaxonomyJobProgress)
    pollIntervalRef.current = setInterval(fetchStatus, 5000);

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, fetchStatus]);

  // Cancel job
  const cancel = useCallback(async () => {
    if (!jobId) return;

    try {
      await apiClient.post(`/brand/extract/cancel/${jobId}`);

      // Refetch status after cancellation
      await fetchStatus();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to cancel job');
    }
  }, [jobId, fetchStatus]);

  // Prioritize job
  const prioritize = useCallback(async () => {
    if (!jobId) return;

    try {
      await apiClient.post(`/brand/extract/prioritize/${jobId}`);

      // Refetch status after prioritization
      await fetchStatus();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to prioritize job');
    }
  }, [jobId, fetchStatus]);

  return {
    status,
    isLoading,
    error,
    cancel,
    prioritize,
    refetch: fetchStatus,
  };
}
